import {useCallback, useMemo} from 'react';
import {Body1, Body2} from '@ui/atoms/typography';
import {SelectField} from '@ui/molecules/select/selectfield';
import {RadioSm} from '@ui/atoms/radio';
import {Checkbox} from '@ui/atoms/checkbox';
import {TimeSelect} from '@ui/atoms/time-select';
import {CadenceStepProps, DayOfWeek} from './types';
import {
  CadenceCard,
  CadenceCardBody,
  CardHeader,
  DayCheckboxLabel,
  DayGrid,
  DayRadioLabel,
  FormGroup,
  ManagerTimeNote,
  MonthlyInlineSelect,
  SendOnContent,
  SendOnHeader,
  SendOnSection,
  TimeGroup,
  TimeRow,
  TimeSelectContainer,
} from './cadence-step.styles';
import {
  DAILY_CADENCE_DAYS,
  UI_SELECTED_DAYS_FREQUENCY,
} from './cadence-frequency';

const frequencyOptions = [
  {label: 'Daily', value: 'daily'},
  {label: 'Selected days', value: UI_SELECTED_DAYS_FREQUENCY},
  {label: 'Weekly', value: 'weekly'},
  {label: 'Bi-weekly', value: 'bi-weekly'},
  {label: 'Monthly', value: 'monthly'},
  {label: 'One-time', value: 'one-time'},
];

const baseDayOfMonthOptions = Array.from({length: 28}, (_, index) => {
  const value = index + 1;
  return {label: String(value), value: String(value)};
});

export const cadenceDays: DayOfWeek[] = DAILY_CADENCE_DAYS;

export const CadenceScheduleCard = ({
  compact = false,
  value,
  onChange,
}: CadenceStepProps) => {
  const {
    frequency,
    selectedDay,
    selectedDays,
    selectedTime,
    monthlySpecificDay,
    timezone,
  } = value;

  const updateField = useCallback(
    <K extends keyof typeof value>(field: K, fieldValue: typeof value[K]) => {
      onChange({...value, [field]: fieldValue});
    },
    [onChange, value],
  );

  const updateSelectedTime = (
    timeValue: string,
    field: keyof typeof selectedTime,
    _type: 'starts' | 'ends',
    period?: string,
  ) => {
    if (field === 'error') {
      updateField('selectedTime', {...selectedTime, error: timeValue});
      return;
    }

    if (field === 'time') {
      const resolvedPeriod = period?.toUpperCase();
      updateField('selectedTime', {
        ...selectedTime,
        error: '',
        period:
          resolvedPeriod === 'PM'
            ? 'PM'
            : resolvedPeriod === 'AM'
            ? 'AM'
            : selectedTime.period,
        time: timeValue,
      });
      return;
    }

    const resolvedPeriod = (
      period ||
      (field === 'period' ? timeValue : selectedTime.period) ||
      'AM'
    ).toUpperCase();

    updateField('selectedTime', {
      ...selectedTime,
      error: '',
      period: resolvedPeriod === 'PM' ? 'PM' : 'AM',
    });
  };

  const handleFrequencyChange = (nextFrequency: string) => {
    if (nextFrequency === 'daily') {
      onChange({
        ...value,
        frequency: nextFrequency,
        selectedDays: [...DAILY_CADENCE_DAYS],
      });
      return;
    }

    if (nextFrequency === UI_SELECTED_DAYS_FREQUENCY) {
      onChange({
        ...value,
        frequency: nextFrequency,
        selectedDays: frequency === 'daily' ? [] : selectedDays,
      });
      return;
    }

    onChange({...value, frequency: nextFrequency});
  };

  const sendOnLabel = useMemo(() => {
    if (frequency === 'daily') {
      return 'Send every day';
    }
    if (frequency === UI_SELECTED_DAYS_FREQUENCY) {
      return 'Send on (select one or more days):';
    }
    if (frequency === 'weekly' || frequency === 'bi-weekly') {
      return 'Send on (select one day):';
    }
    return 'Send on';
  }, [frequency]);

  const toggleDailyDay = (day: DayOfWeek) => {
    const currentDays = selectedDays || [];
    updateField(
      'selectedDays',
      currentDays.includes(day)
        ? currentDays.filter((item) => item !== day)
        : [...currentDays, day],
    );
  };

  const monthlyDayOptions = useMemo(() => {
    const loaded = Number(monthlySpecificDay);
    if (loaded > 28 && loaded <= 31) {
      return [
        ...baseDayOfMonthOptions,
        {label: String(loaded), value: String(loaded)},
      ];
    }
    return baseDayOfMonthOptions;
  }, [monthlySpecificDay]);

  const dayControls =
    frequency === UI_SELECTED_DAYS_FREQUENCY
      ? cadenceDays.map((day) => {
          const checked = selectedDays?.includes(day) || false;
          return (
            <DayCheckboxLabel key={day}>
              <Body2 kind="blackTwo" weight="semibold">
                {day}
              </Body2>
              <Checkbox
                aria-label={`${day} for Selected days`}
                boxSize="small"
                checked={checked}
                onChange={() => toggleDailyDay(day)}
              />
            </DayCheckboxLabel>
          );
        })
      : cadenceDays.map((day) => (
          <DayRadioLabel key={day}>
            <Body2 kind="blackTwo" weight="semibold">
              {day}
            </Body2>
            <RadioSm
              aria-label={`${day} for ${frequency}`}
              name="cadence-day"
              checked={selectedDay === day}
              onChange={() => updateField('selectedDay', day)}
            />
          </DayRadioLabel>
        ));

  return (
    <CadenceCard>
      <CardHeader $compact={compact}>
        <Body1 weight="bold" kind="blackTwo">
          Cadence
        </Body1>
        <Body2 kind="textBody">Set how often this check-in goes out.</Body2>
      </CardHeader>

      <CadenceCardBody $compact={compact}>
        <FormGroup>
          <SelectField
            label="How often?"
            options={frequencyOptions}
            value={frequency}
            borderRadius="10px"
            margin
            excludeSearchBox
            onChange={(option: any) =>
              handleFrequencyChange(option?.value || '')
            }
            placeholder="Select frequency...."
            inputStyle={{borderRadius: '10px'}}
          />

          {frequency && frequency !== 'one-time' && (
            <SendOnSection>
              <SendOnHeader $compact={compact}>
                <Body2 weight="semibold" kind="blackTwo">
                  {sendOnLabel}
                </Body2>
              </SendOnHeader>

              <SendOnContent $compact={compact}>
                {frequency === 'monthly' ? (
                  <div className="flex items-center gap-2 min-h-[40px] flex-wrap">
                    <Body2 weight="semibold" kind="blackTwo">
                      Day
                    </Body2>
                    <MonthlyInlineSelect width="86px">
                      <SelectField
                        options={monthlyDayOptions}
                        value={monthlySpecificDay}
                        onChange={(option: any) => {
                          onChange({
                            ...value,
                            monthlyOption: 'specificDay',
                            monthlySpecificDay: option?.value || '',
                          });
                        }}
                        placeholder="1"
                        margin
                        excludeSearchBox
                        borderRadius="10px"
                        inputStyle={{borderRadius: '10px'}}
                      />
                    </MonthlyInlineSelect>
                    <Body2 weight="semibold" kind="blackTwo">
                      of the Month
                    </Body2>
                  </div>
                ) : frequency === 'daily' ? null : (
                  <DayGrid>{dayControls}</DayGrid>
                )}

                <TimeGroup>
                  <Body2 weight="semibold" kind="blackTwo">
                    Send time
                  </Body2>
                  <TimeRow>
                    <TimeSelectContainer
                      role="group"
                      aria-label="Send time controls">
                      <TimeSelect
                        selectedTime={selectedTime}
                        updateSelectedTime={updateSelectedTime}
                        type="starts"
                        hideLabel
                        borderless
                        noPadding
                        compact
                      />
                    </TimeSelectContainer>
                    <ManagerTimeNote>
                      <Body2 kind="textBody">
                        (
                        {timezone === 'workspace'
                          ? 'workspace time'
                          : "each participant's local time"}
                        )
                      </Body2>
                    </ManagerTimeNote>
                  </TimeRow>
                </TimeGroup>
              </SendOnContent>
            </SendOnSection>
          )}
        </FormGroup>
      </CadenceCardBody>
    </CadenceCard>
  );
};
